import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { Observable, of, timer } from "rxjs";
import { catchError, map, switchMap, take, timeout } from "rxjs/operators";
import { AuthService } from "../services/auth.service";

@Injectable()
export class AuthGuardWithForcedLogin {
	constructor(private authService: AuthService) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		Sentry.addBreadcrumb({ category: "auth", message: `Guard checking for: ${state.url}`, level: "info" });

		// First check if auth service is done loading
		// If not done after 5 seconds, proceed anyway
		return this.authService.isDoneLoading$.pipe(
			switchMap((isDone) => {
				if (isDone) {
					return of(true);
				}
				// Wait up to 5 seconds for loading to complete
				return timer(100, 100).pipe(
					switchMap(() => this.authService.isDoneLoading$),
					take(50), // 50 * 100ms = 5 seconds max
					map((done) => done),
					timeout(5000),
					catchError(() => of(true)), // Proceed anyway after timeout
				);
			}),
			take(1),
			switchMap(() => this.authService.isAuthenticated$.pipe(take(1))),
			map((isAuthenticated) => {
				Sentry.addBreadcrumb({ category: "auth", message: `Authentication status: ${isAuthenticated}`, level: "info" });

				if (!isAuthenticated) {
					// Don't redirect if we're coming from callback or test-auth
					if (!state.url.includes("/callback") && !state.url.includes("/test-auth")) {
						Sentry.addBreadcrumb({
							category: "auth",
							message: "Not authenticated, initiating login...",
							level: "warning",
						});
						// Defer login redirect to avoid navigation conflict
						Promise.resolve().then(() => this.authService.login(state.url));
					}
					return false;
				}

				return true;
			}),
			catchError((err) => {
				Sentry.captureMessage(`Guard error: ${err}`, { level: "error" });
				return of(false);
			}),
		);
	}
}
